import { Percent, ShieldCheck, Unlock, Users, GraduationCap, Send } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";
import Reveal from "@/components/landing/Reveal";

const REASONS = [
  {
    icon: Percent,
    title: "Come veniamo remunerati?",
    desc: "TheGoldFx può ricevere una commissione pari al 10% degli eventuali risultati positivi generati, secondo le condizioni applicabili al servizio. La commissione non viene calcolata sul capitale depositato. I fondi rimangono sul conto dell'utente presso il broker. Consulta i Termini e Condizioni per conoscere nel dettaglio il funzionamento della remunerazione e gli eventuali rapporti commerciali con soggetti terzi.",
    accent: "gold",
    testid: "reason-card-earnings",
  },
  {
    icon: ShieldCheck,
    title: "Trasparente e strutturato",
    desc: "Sistemi con gestione del rischio su ogni operazione e trasparenza al 100% su come funziona il software. Il trading comporta sempre un rischio di perdita del capitale investito.",
    accent: "cyan",
    testid: "reason-card-transparent",
  },
  {
    icon: Unlock,
    title: "Nessun vincolo",
    desc: "Non esiste alcun vincolo di permanenza. Depositi e prelievi sono effettuabili in ogni momento in autonomia.",
    accent: "gold",
    testid: "reason-card-no-lockin",
  },
  {
    icon: Users,
    title: "Team di esperti",
    desc: "TheGoldFx è formata da un team di trader e analisti, a tua disposizione per ogni evenienza. Tutti i nostri sistemi sono sviluppati da trader con esperienza decennale.",
    accent: "cyan",
    testid: "reason-card-experts",
  },
  {
    icon: GraduationCap,
    title: "Configurazione guidata",
    desc: "Non hai mai fatto trading? Nessun problema. Colleghiamo il nostro software al tuo conto trading e ti accompagniamo passo passo nella comprensione di come funziona.",
    accent: "gold",
    testid: "reason-card-beginners",
  },
];

export default function WhyChooseUs() {
  const { openModal } = useLeadModal();

  return (
    <section
      id="perche-noi"
      data-testid="why-choose-us-section"
      className="relative py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mb-12 max-w-3xl text-center sm:mb-16 mx-auto">
            <div className="mb-4 inline-flex items-center gap-2">
              <span className="h-px w-8 bg-gradient-to-r from-transparent to-cyan-400" />
              <span className="font-heading text-[11px] font-bold uppercase tracking-[0.3em] text-cyan-300">
                Il nostro vantaggio
              </span>
              <span className="h-px w-8 bg-gradient-to-l from-transparent to-cyan-400" />
            </div>
            <h2
              data-testid="why-choose-us-title"
              className="font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Perché i nostri sistemi sono{" "}
              <span className="text-gradient-gold">la scelta migliore</span> per te?
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
          {REASONS.map((reason, idx) => {
            const Icon = reason.icon;
            const isGold = reason.accent === "gold";
            return (
              <Reveal key={reason.title} delay={idx * 80}>
                <div
                  data-testid={reason.testid}
                  className={`group relative h-full overflow-hidden rounded-[28px] border bg-[#0a0a0a]/80 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 ${
                    isGold
                      ? "border-yellow-400/20 hover:border-yellow-400/60"
                      : "border-cyan-400/20 hover:border-cyan-400/60"
                  }`}
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(10,10,10,0.95) 0%, rgba(20,20,20,0.85) 100%)",
                  }}
                >
                  <div
                    className={`pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-70 ${
                      isGold ? "bg-yellow-400/30" : "bg-cyan-400/25"
                    }`}
                  />
                  <div
                    className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border ${
                      isGold
                        ? "border-yellow-400/40 bg-yellow-400/10"
                        : "border-cyan-400/40 bg-cyan-400/10"
                    }`}
                  >
                    <Icon className={`h-7 w-7 ${isGold ? "text-yellow-300" : "text-cyan-300"}`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white sm:text-xl">
                    {reason.title}
                  </h3>
                  <p className="mt-3 font-body text-sm text-gray-400 sm:text-base">
                    {reason.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center sm:mt-16">
          <button
            type="button"
            onClick={openModal}
            data-testid="why-choose-us-cta"
            className="group inline-flex w-full max-w-md items-center justify-center gap-3 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600 px-8 py-5 font-heading text-base font-black uppercase tracking-wider text-black shadow-[0_0_40px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(255,215,0,0.75)] sm:text-lg"
          >
            <Send className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            Richiedi informazioni
          </button>
        </div>
      </div>
    </section>
  );
}
